import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Flame, Megaphone, Home, FolderKanban, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { useActiveSubscription } from '@/hooks/useActiveSubscription';
import { formatDistanceToNow } from 'date-fns';

function Meter({ icon: Icon, label, used, quota }: { icon: any; label: string; used: number; quota: number }) {
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const danger = quota > 0 && used >= quota;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </span>
        <span className={`font-semibold tabular-nums ${danger ? 'text-destructive' : ''}`}>
          {used} / {quota || '—'}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}

export function SubscriptionStatusCard() {
  const sub = useActiveSubscription();

  if (sub.isLoading) {
    return <Card><CardContent className="p-5 h-40 animate-pulse bg-muted/40 rounded-2xl" /></Card>;
  }

  if (!sub.hasActive) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="p-5 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-destructive/10 p-2"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
            <div>
              <p className="font-semibold">No active subscription</p>
              <p className="text-sm text-muted-foreground">Subscribe to a package to publish listings, run banner ads, and boost search visibility.</p>
            </div>
          </div>
          <Button asChild><Link to="/dashboard/subscription">View Packages</Link></Button>
        </CardContent>
      </Card>
    );
  }

  const pkg = sub.primary!;
  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/[0.04] to-transparent">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="h-4 w-4 text-primary" />
              <p className="font-semibold">{pkg.package_name}</p>
              <Badge variant="outline" className="text-[10px] capitalize">{pkg.audience}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Renews {formatDistanceToNow(new Date(pkg.expires_at), { addSuffix: true })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm" className="gap-1">
              <Link to="/dashboard/subscription">Manage <ArrowUpRight className="h-3 w-3" /></Link>
            </Button>
            <Button asChild size="sm" className="gap-1">
              <Link to="/dashboard/subscription">Upgrade</Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Meter icon={Home} label="Listings" used={sub.listings.used} quota={sub.listings.quota} />
          <Meter icon={FolderKanban} label="Projects" used={sub.projects.used} quota={sub.projects.quota} />
          <Meter icon={Flame} label="Search Boosts" used={sub.boosts.used} quota={sub.boosts.quota} />
          <Meter icon={Megaphone} label="Banner Ads" used={sub.banners.used} quota={sub.banners.totalQuota} />
        </div>
      </CardContent>
    </Card>
  );
}
